import { Check, CircleDashed, Minus, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useReadOnly } from "@/lib/read-only";
import { cn } from "@/lib/utils/cn";

import { ANSWER_VALUES, type AnswerType } from "../types";

interface Option {
  value: AnswerType;
  labelKey: string;
  icon: LucideIcon;
  /** Classes applied when this option is selected (semantic colour). */
  selected: string;
}

/**
 * Semantic answer colours are separate from the thematic identity accent:
 *   carried out  → success green,   course of action → economic cyan (planned),
 *   not applicable → neutral slate. State reads from colour + icon, not the
 *   radio dot alone.
 */
const OPTIONS: Option[] = [
  {
    value: ANSWER_VALUES.CarriedOut,
    labelKey: "questionnaire.answers.carriedOut",
    icon: Check,
    selected: "border-success bg-success/10 text-success",
  },
  {
    value: ANSWER_VALUES.CourseOfAction,
    labelKey: "questionnaire.answers.courseOfAction",
    icon: CircleDashed,
    selected: "border-theme-economic-ink bg-theme-economic-soft text-theme-economic-ink",
  },
  {
    value: ANSWER_VALUES.NotApplicable,
    labelKey: "questionnaire.answers.notApplicable",
    icon: Minus,
    selected: "border-muted-foreground/50 bg-muted text-muted-foreground",
  },
];

interface SegmentedAnswerProps {
  value: AnswerType | null;
  onChange: (value: AnswerType) => void;
  ariaLabel: string;
}

/** Three-way answer selector: Carried out / Course of action / Not applicable. */
export function SegmentedAnswer({ value, onChange, ariaLabel }: SegmentedAnswerProps) {
  const { t } = useTranslation();
  // A partner consulting a member without edit consent sees the answers but
  // can't change them (G-11).
  const readOnly = useReadOnly();
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="grid gap-2 sm:grid-cols-3"
    >
      {OPTIONS.map((opt) => {
        const checked = value === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={readOnly}
            onClick={() => onChange(opt.value)}
            className={cn(
              "flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors",
              checked
                ? opt.selected
                : "border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
              readOnly && "cursor-not-allowed opacity-70 hover:bg-background",
            )}
          >
            <Icon className="size-4 shrink-0" aria-hidden />
            {t(opt.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
